import React, { useEffect, useState } from 'react';
import { withRouter, Link } from 'react-router-dom';
import { Printer } from 'react-feather';

import MainHeader from '../../../components/main-header/main-header.component';
import Mainmenu from '../../../components/menu/main-menu/main-menu.component';

import Footer from '../../../components/footer/footer.component';

import {
  Select,
  SelectItem,
  DatePicker,
  DatePickerInput,
  Pagination,
} from 'carbon-components-react';

import { createStructuredSelector } from 'reselect';
import { selectCurrentUser } from '../../../redux/selectors/user.selectors';
import {
  selectClasses,
  selectClassLabels,
} from '../../../redux/selectors/class.selectors';
import {
  setClasses,
  setClassLabels,
} from '../../../redux/actions/class.actions';

import { getClasses, getClassLabels } from '../../../api/class.service';

import { connect, useSelector } from 'react-redux';
import { formatDate, getQueryString } from '../../../utils/common.util';
import LoaderPane from '../../../components/loader-pane/loader-pane.component';

import './students-attendance-report.styles.scss';
import useSWR from 'swr';
import CustomButton from '../../../components/custom-button/custom-btn.component';
import TemplateModal from '../../../components/modal/template/template.component';
import {
  selectAcademicYears,
  selectTerms,
} from '../../../redux/selectors/academic-year.selectors';
import fileDownload from 'js-file-download';
import { fetchService } from '../../../api/common';
import { useQueryParams } from '../../../utils/useQueryParams';
import { useUrlState } from '../../../utils/useUrlState';
import { useDebounceState } from '../../../utils/useDebounceState';
import Combobox from '../../../components/combobox/Combobox';

const StudentsAttendanceReportPage = (props) => {
  const { authToken } = useSelector(selectCurrentUser);
  const academicYears = useSelector(selectAcademicYears);
  const terms = useSelector(selectTerms);

  const {
    studentId: urlStudentId,
    classId: urlClassId,
    classDescription: urlClassDescription,
  } = useQueryParams('studentId', 'classId', 'classDescription');

  const [query, setQuery] = useUrlState({
    studentId: urlStudentId ?? '',
    classId: urlClassId ?? '',
    classDescription: urlClassDescription ?? '',
    academicYearId: '',
    termId: '',
    status: '',
    fromDate: '',
    toDate: '',
  });

  const resolvedClassId = query.classId
    ? query.classId
    : query.classDescription && props.classes?.length
      ? props.classes.find((c) => c.description === query.classDescription)?.id || ''
      : '';

  const [studentSearch, setStudentSearch] = useDebounceState('');
  const { data: students, isLoading: isStudentsLoading } = useSWR(
    resolvedClassId
      ? `student/list?status=Enrolled&${getQueryString({
          query: studentSearch,
          classId: resolvedClassId,
        })}`
      : null,
  );

  const [classLabelId, setClassLabelId] = useState();
  const [paginate, setPaginate] = useState({
    page: 1,
    pageSize: 20,
  });
  const [isReady, setIsReady] = useState(false);
  const [exporting, setExporting] = useState('');
  const [exportingError, setExportingError] = useState('');
  const [openPrintModal, setOpenPrintModal] = useState(false);

  useEffect(() => {
    if (props.classes.length === 0) {
      getClasses(props.currentUser.authToken).then((response) => {
        if (response.success) {
          props.setClasses(response.classes);
        }
      });
    }

    if (props.classLabels.length === 0) {
      getClassLabels(props.currentUser.authToken).then((response) => {
        if (response.success) {
          props.setClassLabels(response.classLabels);
        }
      });
    }
  }, []);

  const buildUrl = (isExporting = false) => {
    return `students-attendance/attendance-sheet${isExporting ? '/export' : ''}?${getQueryString(
      {
        fromDate: query.fromDate,
        toDate: query.toDate,
        classId: resolvedClassId,
        classLabelId,
        attendanceStatus: query.status,
        studentId: query.studentId,
        termId: query.termId,
        academicYearId: query.academicYearId,
        take: paginate.pageSize,
        skip: (paginate.page - 1) * paginate.pageSize,
      },
    )}`;
  };
  const { data, error, mutate } = useSWR(isReady && buildUrl());
  const totals = data?.extendedData || {};

  const exportReport = async () => {
    setExporting(true);
    setExportingError(null);

    const response = await fetchService(buildUrl(true), authToken);

    if (response.success) {
      setExporting(false);
      fileDownload(
        response.data.data,
        `students-attendance-report${new Date()}.xlsx`,
      );

      return;
    }

    setExporting(false);
    setExportingError(response.message || 'Exporting failed');
  };

  const filterTexts = [
    {
      label: 'Academic Year',
      text:
        academicYears?.find((ac) => ac.id == query.academicYearId)
          ?.description || 'All',
    },
    {
      label: 'Term',
      text: terms?.find((t) => t.id == query.termId)?.description || 'All',
    },
    {
      label: 'Class',
      text: `${props?.classes?.find((c) => c.id == resolvedClassId)?.description || 'All'}${props.classLabels.find((cl) => cl.id == classLabelId) ? ` ${props.classLabels.find((cl) => cl.id == classLabelId)?.description}` : ''}`,
    },
    {
      label: 'Student',
      text: query.studentId
        ? students?.find((s) => s.id == query.studentId)
          ? `${students.find((s) => s.id == query.studentId)?.firstName} ${students.find((s) => s.id == query.studentId)?.lastName}`
          : 'All'
        : 'All',
    },
    {
      label: 'Status',
      text: query.status || 'All',
    },
  ];

  const title = `Students Attendance Report${query.fromDate && query.toDate ? `: ${new Date(query.fromDate).toDateString()} - ${new Date(query.toDate).toDateString()}` : ''}`;

  return (
    <div>
      <MainHeader />

      {openPrintModal && (
        <TemplateModal
          title={title}
          school={props.currentUser.school}
          topContentData={filterTexts}
          content={<TableContent data={data} />}
          open={true}
          closeAction={() => setOpenPrintModal(false)}
        />
      )}

      <div className="students-attendance page-container">
        <div>
          <p>
            <span className="text-12 font-bold text-grey">You are here</span>
            &nbsp;&nbsp;
            <Link to="/analytics" className="text-12 text-dark">
              Analytics
            </Link>
            &nbsp;<span className="text-12">&gt;</span>&nbsp;
            <span className="text-12 text-primary font-bold">
              Students Attendance Report
            </span>
          </p>
        </div>
        <div>
          <span className="right">
            <Mainmenu />
          </span>
        </div>

        <hr className="grey-hr mt-20" />

        <div className="h-24 uppercase mt-15">{title}</div>

        {/* filters */}
        <div className="mt-20 bx--row">
          {/* academic year */}
          <div className="bx--col-md-2 npr mb-20">
            <Select
              labelText="Academic Year"
              onChange={(e) =>
                setQuery({
                  academicYearId: e.target.value,
                  termId: '',
                })
              }
              value={query.academicYearId}
            >
              <SelectItem value="" text="" />
              {academicYears.map((year) => (
                <SelectItem
                  key={year.id}
                  value={year.id}
                  text={year.description}
                />
              ))}
            </Select>
          </div>
          {/* terms */}
          <div className="bx--col-md-2 npr mb-20">
            <Select
              disabled={!query.academicYearId}
              labelText="Term"
              onChange={(e) => setQuery({ termId: e.target.value })}
              value={query.termId}
            >
              <SelectItem value="" text="" />
              {terms
                .filter((m) => m.academicYearId == query.academicYearId)
                .map((term) => (
                  <SelectItem
                    key={term.id}
                    value={term.id}
                    text={term.description}
                  />
                ))}
            </Select>
          </div>
          {/* class */}
          <div className="bx--col-md-2 npr mb-20">
            <Select
              labelText="Class"
              onChange={(e) =>
                setQuery({ classId: e.target.value, classDescription: '' })
              }
              value={resolvedClassId}
            >
              <SelectItem value="" text="" />
              {props.classes?.map((item) => (
                <SelectItem
                  key={item.id}
                  text={item.description}
                  value={item.id}
                />
              ))}
            </Select>
          </div>
          {/* class label  */}
          <div className="bx--col-md-2 npr mb-20">
            <Select
              disabled={!resolvedClassId}
              labelText="Class label"
              onChange={(e) => setClassLabelId(e.target.value)}
              value={classLabelId}
            >
              <SelectItem value="" text="" />
              {props.classLabels?.map((item) => (
                <SelectItem
                  key={item.id}
                  text={item.description}
                  value={item.id}
                />
              ))}
            </Select>
          </div>
          {/* student */}
          <div className="bx--col-md-2 npr mb-20">
            <Combobox
              options={students ?? []}
              label="Select a Student"
              value={
                query.studentId
                  ? students?.find((s) => s.id == query.studentId)
                  : null
              }
              onChange={(option) => {
                setQuery({ studentId: option ? option.id : '' });
                setStudentSearch('');
              }}
              onSearch={setStudentSearch}
              onClear={() => {
                setQuery({ studentId: '' });
                setStudentSearch('');
              }}
              loading={isStudentsLoading}
              renderSelected={(option) => `${option.firstName} ${option.lastName}`}
              renderOption={(option) => `${option.firstName} ${option.lastName}`}
              labelKey="fullName"
              valueKey="id"
              searchPlaceholder="Search students..."
              placeholder=""
            />
          </div>
          {/* start date */}
          <div className="bx--col-md-2 npr">
            <DatePicker
              dateFormat="Y-m-d"
              datePickerType="single"
              id="start-date-picker"
              onChange={(value) => {
                if (value.length > 0) {
                  setQuery({
                    fromDate: [
                      value[0].getFullYear(),
                      ('0' + (value[0].getMonth() + 1)).slice(-2),
                      ('0' + value[0].getDate()).slice(-2),
                    ].join('-'),
                  });
                } else setQuery({ fromDate: '' });
              }}
              onClose={(value) => {}}
            >
              <DatePickerInput
                className="start-date"
                id="start-date"
                name="startDate"
                labelText="Start date"
                value={query.fromDate}
                pattern="d{1,2}/d{4}"
                placeholder="YYYY-MM-DD"
                type="text"
                onChange={(e) => {
                  setQuery({ fromDate: e.target.value });
                }}
              />
            </DatePicker>
          </div>
          {/* end date */}
          <div className="bx--col-md-2 npr">
            <DatePicker
              dateFormat="Y-m-d"
              datePickerType="single"
              id="end-date-picker"
              onChange={(value) => {
                if (value.length > 0) {
                  setQuery({
                    toDate: [
                      value[0].getFullYear(),
                      ('0' + (value[0].getMonth() + 1)).slice(-2),
                      ('0' + value[0].getDate()).slice(-2),
                    ].join('-'),
                  });
                } else setQuery({ toDate: '' });
              }}
              onClose={(value) => {}}
            >
              <DatePickerInput
                className="end-date"
                id="end-date"
                name="endDate"
                labelText="End date"
                value={query.toDate}
                pattern="d{1,2}/d{4}"
                placeholder="YYYY-MM-DD"
                type="text"
                onChange={(e) => setQuery({ toDate: e.target.value })}
                disabled={!query.fromDate}
              />
            </DatePicker>
          </div>
          {/* status */}
          <div className="bx--col-md-2 npr  mb-20">
            <Select
              labelText="Status"
              onChange={(e) => setQuery({ status: e.target.value })}
              value={query.status}
            >
              <SelectItem value="" text="" />
              <SelectItem text={'Present'} value={'Present'} />
              <SelectItem text={'Absent'} value={'Absent'} />
              <SelectItem value="Late" text="Late" />
              <SelectItem value="Excused" text="Excused" />
            </Select>
          </div>

          {/* run report button  */}
          <div className="bx--col-md-2 npr">
            <CustomButton
              classes="f-width primary mt-25 font-bold"
              disabled={!resolvedClassId}
              onClick={() => setIsReady(true)}
            >
              RUN REPORT
            </CustomButton>
          </div>
        </div>

        <LoaderPane
          loading={!data && !error && isReady}
          error={error?.message}
          noRecords={data?.data?.length === 0}
          onReload={() => mutate()}
        />

        {!resolvedClassId && (
          <div className="text-center font-500 text-primary text-danger text-20 mt-10">
            You must select at least a class to generate report.
          </div>
        )}

        {data?.data?.length > 0 && (
          <div className="mt-20">
            <div className="d-flex mt-20 mb-10" style={{ gap: '20px' }}>
              <span>Total: {data?.data.length ?? 0}</span>
              <span>Present: {totals?.totalStudentsPresent ?? 0}</span>
              <span>Absent: {totals?.totalStudentsAbsent ?? 0}</span>
              <span>Late: {totals?.totalStudentsLate ?? 0}</span>
              <span>Excused: {totals?.totalStudentsExcused ?? 0}</span>
            </div>
            <TableContent data={data} />
            <Pagination
              backwardText="Previous page"
              forwardText="Next page"
              page={paginate.page}
              pageNumberText="Page Number"
              pageSize={paginate.pageSize}
              pageSizes={[10, 20, 30, 40, 50, 100]}
              totalItems={data?.pagingInfo?.recordsCount}
              onChange={(value) => setPaginate(value)}
            />
          </div>
        )}

        {data?.data?.length > 0 && (
          <div className="mt-35">
            {exportingError && (
              <div className="text-danger mb-10">{exportingError}</div>
            )}
            <div className="bx--row mt-10">
              <div className="bx--col-lg-3">
                <CustomButton
                  classes="secondary f-width font-bold"
                  disabled={exporting}
                  onClick={exportReport}
                >
                  {exporting ? (
                    'EXPORTING...'
                  ) : (
                    <span>
                      <Printer size="11" strokeWidth="5px" />
                      &nbsp;EXPORT TO EXCEL
                    </span>
                  )}
                </CustomButton>
              </div>
              <div className="bx--col-lg-2">
                <CustomButton
                  classes="primary f-width font-bold"
                  onClick={() => setOpenPrintModal(true)}
                >
                  <Printer size="11" strokeWidth="5px" /> &nbsp; PRINT
                </CustomButton>
              </div>
            </div>
          </div>
        )}

        <Footer fixed={false} showLinks={false} />
      </div>
    </div>
  );
};

const TableContent = ({ data }) => (
  <div className="table-container">
    <table className="bx--data-table table-white">
      <thead>
        <tr>
          <th>Date</th>
          <th>Unique Id</th>
          <th>Name</th>
          <th>Status</th>
          <th>Class</th>
        </tr>
      </thead>
      <tbody>
        {data?.data.map((attendance, i) => (
          <tr key={i}>
            <td>{formatDate(attendance.date)}</td>
            <td>{attendance.studentNumber}</td>
            <td>{attendance?.name}</td>
            <td className="font-bold">
              <span
                className={`${
                  attendance.status === 'Present'
                    ? 'text-success'
                    : 'text-danger'
                }`}
              >
                {attendance.status}
              </span>
            </td>
            <td>
              {attendance?.className} {attendance?.classLabel}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// this will provide us with the state values we need
const mapStateToProps = createStructuredSelector({
  currentUser: selectCurrentUser,
  classes: selectClasses,
  classLabels: selectClassLabels,
});

// this allows us to map our redux actions to our component
const mapDispatchToProps = (dispatch) => ({
  setClasses: (classes) => dispatch(setClasses(classes)),
  setClassLabels: (classLabels) => dispatch(setClassLabels(classLabels)),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(StudentsAttendanceReportPage),
);
